import { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon } from '@src/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@src/components/icons/ChevronRightIcon';
import { Button } from '@src/components/Button/Button';
import { Cluster } from '@src/components/Cluster/Cluster';
import { Typography } from '@src/components/Typography/Typography';
import { testIdAttr, testIdFor } from '@src/components/testId';
import './Pagination.css';

const GAP = 'gap' as const;

export interface PaginationProps {
  /** 1-based. */
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
  previousLabel: string;
  nextLabel: string;
  /** Called with the page number, for the accessible name of each button. */
  pageLabel: (page: number) => string;
  testId?: string;
}

/**
 * First and last page are always reachable, the current one keeps a neighbour
 * on each side, and the rest collapses into an ellipsis — so the control stays
 * the same width whether there are three pages or thirty.
 */
function pagesToShow(page: number, pageCount: number, compact: boolean): (number | typeof GAP)[] {
  if (pageCount <= (compact ? 3 : 7)) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const pages = new Set(compact ? [1, page, pageCount] : [1, pageCount, page, page - 1, page + 1]);
  const shown = [...pages].filter((value) => value >= 1 && value <= pageCount).sort((a, b) => a - b);

  const result: (number | typeof GAP)[] = [];
  shown.forEach((value, index) => {
    if (index > 0 && value - (shown[index - 1] as number) > 1) {
      result.push(GAP);
    }
    result.push(value);
  });
  return result;
}

export function Pagination({
  page,
  pageCount,
  onChange,
  previousLabel,
  nextLabel,
  pageLabel,
  testId,
}: PaginationProps) {
  const navRef = useRef<HTMLElement>(null);
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const observer = new ResizeObserver(() => setCompact(nav.clientWidth < 440));
    observer.observe(nav);
    setCompact(nav.clientWidth < 440);
    return () => observer.disconnect();
  }, [pageCount]);
  if (pageCount <= 1) {
    return null;
  }

  const current = Math.min(Math.max(page, 1), pageCount);

  return (
    <nav ref={navRef} className={`pagination${compact ? ' pagination--compact' : ''}`} aria-label={pageLabel(current)} {...testIdAttr(testId)}>
      <Cluster gap={1} align="center" justify="center" wrap={false}>
        <Button
          variant="ghost"
          size="small"
          icon={<ChevronLeftIcon />}
          aria-label={previousLabel}
          title={previousLabel}
          disabled={current === 1}
          onClick={() => onChange(current - 1)}
          testId={testIdFor(testId, 'previous')}
        />
        {pagesToShow(current, pageCount, compact).map((value, index) =>
          value === GAP ? (
            <Typography
              // Ellipses have nothing to key on but their position.
              key={`gap-${index}`}
              as="span"
              role="meta/helper"
              className="pagination__gap"
            >
              …
            </Typography>
          ) : (
            <Button
              key={value}
              variant={value === current ? 'soft' : 'ghost'}
              size="small"
              aria-label={pageLabel(value)}
              aria-current={value === current ? 'page' : undefined}
              onClick={() => onChange(value)}
              testId={testIdFor(testId, `page-${value}`)}
            >
              {String(value)}
            </Button>
          ),
        )}
        <Button
          variant="ghost"
          size="small"
          icon={<ChevronRightIcon />}
          aria-label={nextLabel}
          title={nextLabel}
          disabled={current === pageCount}
          onClick={() => onChange(current + 1)}
          testId={testIdFor(testId, 'next')}
        />
      </Cluster>
    </nav>
  );
}
